import { useCallback } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { isEqual, isNil } from 'lodash';
import { INDICATION_TYPES } from 'taboola-ultimate-ui';
import { useDynamicReportApi } from '../../../services/api/dynamicReportApi/useDynamicReportApi';
import { COMPONENT_STATUS } from '../../../services/constants';
import { queryClient } from '../../../services/query';
import { isDynamicReport } from '../../campaigns/config/isStaticDynamicReport';
import { getReportError, setReportDataStatus } from '../../campaigns/modules/campaigns-reports/actions';
import { useDateRange } from '../../campaigns/modules/campaigns-reports/hooks';
import {
    alignColumnsToColumnState,
    createDateRange,
    defaultDateRange,
    DYNAMIC_REPORT_FLOW,
    getColumStateByDynamicReport,
    getDynamicReportWithoutWidth,
    getEmptyDynamicReport,
    isAutoFit,
    transformDynamicReportFromGw,
    transformDynamicReportToGw,
} from '../../campaigns/modules/campaigns-reports/services/dynamicReportsService';
import { setDynamicReportDataToCache } from '../../campaigns/modules/campaigns-reports/services/setDynamicReportDataToCache';
import { addIndication } from '../../taboola-common-frontend-modules/Indications';
import { useAccount } from '../../taboola-common-frontend-modules/account-configurations';
import { FormattedMessage, useIntl } from '../../taboola-common-frontend-modules/i18n';
import { NEW_DYNAMIC_REPORT_ID } from '../config/initialValues/newDynamicReportId';
import { DYNAMIC_REPORT_QUERY_PREFIX } from './useDynamicReportCache';
import { useDynamicReportChangeState } from './useDynamicReportChangeState';
import { META_MODEL_TYPE_PERFORMANCE, useDynamicReportMetaModel } from './useDynamicReportMetaModel';

const RETRY_ATTEMPTS = 2;
const HOUR = 60 * 60 * 1000;
export const getSuccessIndication = (reportId, messageId) => ({
    message: <FormattedMessage id={messageId} values={{ reportId }} />,
    type: INDICATION_TYPES.SUCCESS,
    highlight: <FormattedMessage id="success.highlight" defaultMessage="Success!" />,
});
export const getErrorIndication = (reportId, messageId) => ({
    message: <FormattedMessage id={messageId} values={{ reportId }} />,
    type: INDICATION_TYPES.ERROR,
    highlight: <FormattedMessage id="error.highlight" />,
});

export const useDynamicReport = reportId => {
    const { formatMessage } = useIntl();
    const { id, accountName } = useAccount();
    const accountId = `${id}`;
    const enabled = isDynamicReport(reportId) && !!accountName && !isNil(accountName);
    const dispatch = useDispatch();
    const dynamicReportApi = useDynamicReportApi();
    const [, , cleanChangeState] = useDynamicReportChangeState();
    const { isSuccess: metaModelSuccess } = useDynamicReportMetaModel(META_MODEL_TYPE_PERFORMANCE);
    const [{ startDate, endDate, datePreset }] = useDateRange();
    const initialDateRange = datePreset ? createDateRange(startDate, endDate, datePreset) : defaultDateRange;
    const { data, isSuccess } = useQuery(
        [DYNAMIC_REPORT_QUERY_PREFIX, accountId, reportId],
        async () => {
            const result =
                reportId === NEW_DYNAMIC_REPORT_ID
                    ? getEmptyDynamicReport(formatMessage, META_MODEL_TYPE_PERFORMANCE, accountId, initialDateRange)
                    : await dynamicReportApi.getReport(accountName, reportId);
            return transformDynamicReportFromGw(result, { formatMessage });
        },
        {
            onError: error => {
                dispatch(getReportError(error));
                dispatch(setReportDataStatus(COMPONENT_STATUS.ERROR));
                dispatch(addIndication(getErrorIndication(reportId, 'dynamic.reports.dynamic.report.fetch.failure')));
            },
            enabled,
            retry: RETRY_ATTEMPTS,
            cacheTime: HOUR,
            staleTime: HOUR,
        }
    );
    const setReportToCache = useCallback(
        (reportId, newData) => {
            setDynamicReportDataToCache(reportId, accountId, newData);
        },
        [accountId]
    );
    const removeFromCache = useCallback(
        reportId => {
            queryClient.removeQueries([DYNAMIC_REPORT_QUERY_PREFIX, accountId, reportId]);
        },
        [accountId]
    );
    const createReport = useCallback(async () => {
        try {
            const transformedData = transformDynamicReportToGw(data, DYNAMIC_REPORT_FLOW.CREATE);
            const result = await dynamicReportApi.createReport(accountName, transformedData);
            cleanChangeState(reportId);
            dispatch(addIndication(getSuccessIndication(data.name, 'dynamic.reports.dynamic.report.create.success')));
            return result;
        } catch {
            dispatch(addIndication(getErrorIndication(data.name, 'dynamic.reports.dynamic.report.create.failure')));
        }
    }, [data, accountName, reportId, dynamicReportApi, dispatch, cleanChangeState]);
    const updateReport = useCallback(async () => {
        try {
            const transformedData = transformDynamicReportToGw(data, DYNAMIC_REPORT_FLOW.UPDATE);
            const result = await dynamicReportApi.updateReport(accountName, reportId, transformedData);
            cleanChangeState(reportId);
            dispatch(addIndication(getSuccessIndication(data.name, 'dynamic.reports.dynamic.report.update.success')));
            return result;
        } catch {
            dispatch(addIndication(getErrorIndication(data.name, 'dynamic.reports.dynamic.report.update.failure')));
        }
    }, [data, accountName, reportId, dynamicReportApi, dispatch, cleanChangeState]);

    const getColumnState = useCallback(() => getColumStateByDynamicReport(data), [data]);
    const setColumnState = useCallback(
        columnState => {
            const newData = alignColumnsToColumnState(data, columnState);

            const isAutoWidthChange = isAutoFit(columnState);

            if (
                isEqual(data, newData) ||
                (isAutoWidthChange &&
                    isEqual(getDynamicReportWithoutWidth(data), getDynamicReportWithoutWidth(newData)))
            ) {
                return;
            }
            setDynamicReportDataToCache(reportId, accountId, newData);
        },
        [data, accountId, reportId]
    );
    const isDataReady = isSuccess && enabled && metaModelSuccess && !!data?.id;

    return {
        isSuccess: isDataReady,
        dynamicReport: isDataReady ? data : undefined,
        setReportToCache,
        removeFromCache,
        createReport,
        updateReport,
        getColumnState,
        setColumnState,
    };
};
